<template>
  <page>
    <v-card>
      <v-card-title>
        <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class=" mr-2" @click="doNew">
          New Event Type
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
        <download-csv :data="items" name="EventTypeList.csv">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn class="mx-2" fab dark small color="primary" v-on="on">
                <v-icon dark>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Download CSV</span>
          </v-tooltip>
        </download-csv>
        <v-flex xs12>
          <message :messages="messages"></message>
        </v-flex>
      </v-card-title>
      <v-flex xs12>
        <v-card>
          <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            :footer-props="{'items-per-page-options': itemsPerPage }"
            v-bind:options.sync="pagination"
            item-key="name"
            :loading="loading"
            :no-data-text="noDataText"
            :custom-filter="customSearch"
          >
            <template v-slot:item="row"
              ><tr>
                <td class="text-xs-left">{{ row.item.eventType }}</td>
                <td class="text-xs-left">{{ row.item.eventDesc }}</td>
                <td class="table-action-header">
                  <table-action
                    :rowData="row"
                    review
                    @review="doReview(row.item.eventType)"
                    query
                    @query="doQuery(row.item.eventType)"
                    remove
                    @remove="doPromptRemove(row.item)"
                  ></table-action>
                </td></tr
            ></template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-card>
    <v-dialog v-model="eventTypeReview" width="800px" persistent>
      <v-card>
        <v-toolbar v-if="newEventType" dense color="grey lighten-3" flat>
          <v-icon>mdi-plus</v-icon>
          <v-toolbar-title>Create New Event Type</v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-else-if="reviewMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title>Edit Event Type</v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-else-if="queryMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-magnify</v-icon>
          <v-toolbar-title>Query Event Type</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs6 v-if="newEventType">
                <v-text-field
                  :label="labels.eventType"
                  v-model="eventTypeForm.eventType"
                  :error-messages="errors.collect('eventType')"
                  data-vv-name="eventType"
                  :data-vv-as="labels.eventType"
                  v-validate="'required|max:15'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ labels.eventType }}</v-subheader>
                <div class="queryValue">{{ eventTypeForm.eventType }}</div>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  :readonly="queryMode"
                  :label="labels.eventDesc"
                  v-model="eventTypeForm.eventDesc"
                  :error-messages="errors.collect('eventDesc')"
                  data-vv-name="eventDesc"
                  :data-vv-as="labels.eventDesc"
                  v-validate="'required|max:100'"
                />
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ eventTypeForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ eventTypeForm.createdDate | formatDate }}</div>
              </v-flex>
            </v-layout>
            <v-layout wrap v-if="!newEventType">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ eventTypeForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ eventTypeForm.modifiedDate | formatDate }}</div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="doClose">Cancel</v-btn>
          <v-btn v-show="!queryMode" color="primary" text @click.native="doSave">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirm v-model="promptRemove" @ok="doDelete">
      <p>Are you sure you want to delete the selected eventType?</p>
      <div class="queryValue">{{ selectedEventType.eventType }} - {{ selectedEventType.eventDesc }}</div>
    </confirm>
  </page>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'

export default {
  data() {
    return {
      headers: [{ text: 'Event Type', value: 'eventType' }, { text: 'Description', value: 'eventDesc' }, TableConst.tableActionHeader],
      eventTypeReview: false,
      newEventType: false,
      labels: {
        eventType: 'Event Type',
        eventDesc: 'Description',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      eventTypeForm: {
        eventType: null,
        eventDesc: null,
        createdBy: null,
        createdDate: null,
        modifiedBy: null,
        modifiedDate: null
      },
      selectedEventType: {
        eventType: null,
        eventDesc: null
      },
      promptRemove: false,
      queryMode: false,
      reviewMode: false,
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  created() {
    this.doInit()
  },
  methods: {
    doInit() {
      const apiURI = this.menu.getEventTypeList
      if (apiURI) {
        this.loading = true
        this.$http
          .get(apiURI)
          .then((result) => {
            this.items = []
            const { data } = result.data
            if (data) {
              data.map((eventType) => {
                const convertList = {
                  eventType: eventType.eventType,
                  eventDesc: eventType.eventDesc
                }
                this.items.push(convertList)
              })
            }
            this.loading = false
          })
          .catch((err) => {
            this.loading = false
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doNew() {
      this.resetValidator()
      this.resetMessages()
      this.eventTypeForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.eventTypeForm.createdDate = new Date()
      this.eventTypeForm.modifiedBy = null
      this.eventTypeForm.modifiedDate = null
      this.newEventType = true
      this.eventTypeReview = true
      this.queryMode = false
    },
    doReview(eventType) {
      this.doFetchData(eventType)
      this.reviewMode = true
      this.queryMode = false
    },
    doQuery(eventType) {
      this.doFetchData(eventType)
      this.queryMode = true
      this.reviewMode = false
    },
    async doFetchData(eventType) {
      this.resetValidator()
      this.resetMessages()
      if (eventType) {
        this.loading = true
        this.newEventType = false
        const apiURI = this.menu.getEventType
        if (apiURI) {
          this.$http
            .get(expandURI(apiURI, { eventType }))
            .then((result) => {
              const { data } = result.data
              if (data) {
                this.eventTypeForm.eventType = data.eventType.eventType
                this.eventTypeForm.eventDesc = data.eventType.eventDesc
                this.eventTypeForm.createdBy = data.createdBy
                this.eventTypeForm.createdDate = data.createdDate
                this.eventTypeForm.modifiedBy = data.modifiedBy
                this.eventTypeForm.modifiedDate = data.modifiedDate
                this.eventTypeReview = true
              }
              this.loading = false
            })
            .catch((err) => {
              this.loading = false
              const { data } = err.response
              if (data) {
                this.message(data)
                this.fieldMessage(data)
              }
            })
        }
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newEventType) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    doCreate() {
      const apiURI = this.menu.createEventType
      if (apiURI) {
        this.$http
          .post(apiURI, this.eventTypeForm)
          .then((result) => {
            const { data } = result
            if (data) {
              this.notify(data)
              this.doInit()
              this.doClose()
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doUpdate() {
      const apiURI = this.menu.updateEventType
      if (apiURI) {
        this.$http
          .put(apiURI, this.eventTypeForm)
          .then((result) => {
            const { data } = result
            if (data) {
              this.notify(data)
              this.doInit()
              this.doClose()
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    async doPromptRemove(eventType) {
      this.resetMessages()
      if (eventType && eventType.eventType) {
        this.selectedEventType.eventType = eventType.eventType
        this.selectedEventType.eventDesc = eventType.eventDesc
        this.promptRemove = true
      }
    },
    doDelete() {
      this.resetMessages()
      if (this.selectedEventType && this.selectedEventType.eventType) {
        const apiURI = this.menu.deleteEventType
        if (apiURI) {
          const expand = this.selectedEventType
          this.$http
            .post(apiURI, expand)
            .then((result) => {
              const data = result.data
              this.notify(data)
              this.promptRemove = false
              this.doInit()
            })
            .catch((err) => {
              this.promptRemove = false
              const { data } = err.response
              if (data) {
                this.message(data)
              }
            })
        }
      }
    },
    doClose() {
      this.resetValidator()
      this.resetMessages()
      this.eventTypeForm.eventType = null
      this.eventTypeForm.eventDesc = null
      this.eventTypeReview = false
      setTimeout(() => {
        this.newEventType = false
      }, 200)
    }
  },
  computed: {
    menu() {
      return this.$store.state.eventTypeParam.menu
    }
  },
  filters: {
    formatDate(value) {
      return formatDisplayDate(value)
    }
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>

<style>
</style>
